import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X, Sparkles, Loader2, Check, ArrowRight, ArrowLeft, Layers, Target, Anchor, Compass, ThumbsUp, Rocket } from "lucide-react";
import api from "../lib/api";
import SwipeCard from "../components/SwipeCard";
import ActivityIcon from "../components/IconMap";

export default function Assessment() {
  const nav = useNavigate();
  const [step, setStep] = useState("onboarding"); // onboarding | select | phase1 | phase2
  const [areas, setAreas] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [phase1Cards, setPhase1Cards] = useState([]);
  const [phase2Cards, setPhase2Cards] = useState([]);
  const [candidateCount, setCandidateCount] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [index, setIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);
  // veteran entry
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("navy");
  const [vetEnjoy, setVetEnjoy] = useState(null); // null | true | false
  const [vetCode, setVetCode] = useState("");
  const [vetLoading, setVetLoading] = useState(false);
  const [vetError, setVetError] = useState("");
  const [veteranMatch, setVeteranMatch] = useState(null);
  const [codeFocused, setCodeFocused] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/interest-areas"), api.get("/activities"), api.get("/veterans/branches")])
      .then(([a, act, br]) => {
        setAreas(a.data.interest_areas);
        setAllActivities(act.data.activities);
        setBranches(br.data.branches);
      })
      .finally(() => setLoading(false));
  }, []);

  const primaryArea = areas.find((a) => a.id === primary);
  const branchExample = branches.find((b) => b.id === selectedBranch)?.example || "HM, IT, ET";

  const matchVeteranCode = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : vetCode).trim();
    if (!code) return;
    setCodeFocused(false);
    setVetLoading(true);
    setVetError("");
    try {
      const { data } = await api.post("/veterans/lookup", { code, branch: selectedBranch });
      if (!data.matched) {
        setVeteranMatch(null);
        setVetError(`We couldn't match "${data.code}". Try an example like ${branchExample} — or explore by interest instead.`);
        return;
      }
      setVeteranMatch(data);
      setPrimary(data.primary_interest_area.id);
    } catch {
      setVetError("Something went wrong. Please try again.");
    } finally {
      setVetLoading(false);
    }
  };

  const codeOptions = branches.find((b) => b.id === selectedBranch)?.codes || [];
  const codeQuery = vetCode.trim().toLowerCase();
  const codeSuggestions = (codeQuery
    ? codeOptions.filter((c) => c.code.toLowerCase().includes(codeQuery) || c.title.toLowerCase().includes(codeQuery))
    : codeOptions
  ).slice(0, 50);

  const startPhase1 = (primaryId = primary, vetMatch = veteranMatch) => {
    if (!primaryId) return;
    let p1 = allActivities.filter((a) => (a.interest_areas || []).includes(primaryId));
    const rel = vetMatch?.relevant_activity_ids || [];
    if (rel.length) {
      // surface the veteran's top matching activities first (top 3 lead the deck)
      p1 = [...p1].sort((a, b) => {
        const ra = rel.indexOf(a.id) === -1 ? 999 : rel.indexOf(a.id);
        const rb = rel.indexOf(b.id) === -1 ? 999 : rel.indexOf(b.id);
        return ra - rb;
      });
    }
    setPhase1Cards(p1);
    setPhase2Cards([]);
    setIndex(0);
    setResponses([]);
    setStep("phase1");
  };

  const finish = useCallback(async (allResponses) => {
    setSubmitting(true);
    const veteran = veteranMatch
      ? { branch: veteranMatch.branch, code: veteranMatch.code, rating_title: veteranMatch.rating_title }
      : null;
    try {
      await api.post("/assessment/submit", { responses: allResponses, veteran });
      nav("/results");
    } catch {
      setSubmitting(false);
    }
  }, [nav, veteranMatch]);

  const goToPhase2 = useCallback(async (phase1Responses) => {
    setTransitioning(true);
    const loved = phase1Responses
      .filter((r) => r.preference === "like" || r.preference === "superlike")
      .map((r) => r.activity_id);
    const minDelay = new Promise((r) => setTimeout(r, 1800));
    try {
      const [{ data }] = await Promise.all([
        api.post("/assessment/phase2", { loved_activity_ids: loved, primary }),
        minDelay,
      ]);
      if (!data.activities.length) {
        await finish(phase1Responses);
        return;
      }
      setPhase2Cards(data.activities);
      setCandidateCount(data.candidate_count);
      setCandidates(data.candidates || []);
      setIndex(0);
      setStep("phase2");
    } finally {
      setTransitioning(false);
    }
  }, [primary, finish]);

  const currentDeck = step === "phase2" ? phase2Cards : phase1Cards;

  const resetAll = () => {
    setStep("onboarding");
    setPrimary(null);
    setPhase1Cards([]);
    setPhase2Cards([]);
    setCandidates([]);
    setCandidateCount(0);
    setIndex(0);
    setResponses([]);
    setVetEnjoy(null);
    setVetCode("");
    setVetError("");
    setVeteranMatch(null);
    setSelectedBranch("navy");
  };

  const decide = useCallback((preference) => {
    const current = currentDeck[index];
    if (!current) return;
    const nextResponses = [...responses, { activity_id: current.id, preference }];
    setResponses(nextResponses);
    if (index + 1 >= currentDeck.length) {
      if (step === "phase1") {
        goToPhase2(nextResponses);
      } else {
        finish(nextResponses);
      }
    } else {
      setIndex(index + 1);
    }
  }, [currentDeck, index, responses, step, finish, goToPhase2]);

  // ---- Onboarding ----
  if (step === "onboarding") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10" data-testid="onboarding-screen">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-brand-primary border-2 border-black rounded-lg p-1.5 shadow-nb-xs">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-black">TalentSwipe<span className="text-brand-primary">.us</span></span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-black leading-[1.05] mb-3">Let's find careers you'll love.</h1>
          <p className="text-base md:text-lg font-semibold text-slate-600 mb-8 max-w-2xl">
            You'll swipe through real work activities and we'll match you to professions. Pick how you'd like to start.
          </p>
        </motion.div>

        {/* Veteran option — obvious but optional */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="nb-card p-6 bg-brand-purple mb-5"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-white border-2 border-black rounded-lg p-2 shadow-nb-xs">
              <Anchor className="w-6 h-6 text-black" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-black/60">For Veterans · Optional</p>
              <h2 className="font-heading text-2xl font-bold text-black leading-tight">Use your military experience</h2>
            </div>
          </div>
          <p className="font-semibold text-black/70 mb-3">
            Do you <b>enjoy</b> your current or most recent military job? If so, we'll start you with civilian activities just like it.
          </p>
          <div className="flex flex-wrap gap-2 mb-1">
            <button
              data-testid="vet-enjoy-yes"
              onClick={() => setVetEnjoy(true)}
              className={`nb-badge ${vetEnjoy === true ? "bg-brand-yellow" : "bg-white"}`}
            >
              <ThumbsUp className="w-4 h-4" /> Yes, I love it
            </button>
            <button
              data-testid="vet-enjoy-no"
              onClick={() => { setVetEnjoy(false); setVeteranMatch(null); setVetError(""); }}
              className={`nb-badge ${vetEnjoy === false ? "bg-brand-yellow" : "bg-white"}`}
            >
              Not really / N/A
            </button>
          </div>

          {vetEnjoy === false && (
            <p className="text-sm font-semibold text-black/70 mt-3">
              No problem — let's find something you'll enjoy more. Explore by interest below. 👇
            </p>
          )}

          {vetEnjoy === true && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-black/70 mb-2">Pick your branch and enter your job code (MOS / Rating / AFSC / NEC):</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {branches.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`veteran-branch-${b.id}`}
                    onClick={() => { setSelectedBranch(b.id); setVetError(""); setVeteranMatch(null); }}
                    className={`nb-badge ${selectedBranch === b.id ? "bg-brand-yellow" : "bg-white"}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <input
                    data-testid="veteran-code-input"
                    className="nb-input w-full"
                    placeholder={`Search code or title — e.g. ${branchExample}`}
                    value={vetCode}
                    autoComplete="off"
                    onFocus={() => setCodeFocused(true)}
                    onBlur={() => setTimeout(() => setCodeFocused(false), 150)}
                    onChange={(e) => { setVetCode(e.target.value); setVetError(""); setVeteranMatch(null); setCodeFocused(true); }}
                    onKeyDown={(e) => e.key === "Enter" && matchVeteranCode()}
                  />
                  {codeFocused && codeSuggestions.length > 0 && (
                    <div data-testid="code-suggestions" className="absolute z-30 mt-1 w-full border-2 border-black rounded-xl bg-white shadow-nb-sm max-h-60 overflow-auto">
                      {codeSuggestions.map((s) => (
                        <button
                          key={s.code}
                          type="button"
                          data-testid={`code-option-${s.code}`}
                          onMouseDown={() => { setVetCode(s.code); matchVeteranCode(s.code); }}
                          className="w-full text-left px-3 py-2 hover:bg-brand-yellow border-b-2 border-black/10 last:border-0 font-semibold text-sm"
                        >
                          <span className="font-bold">{s.code}</span> <span className="text-black/60">— {s.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  data-testid="veteran-match-btn"
                  onClick={() => matchVeteranCode()}
                  disabled={vetLoading || !vetCode.trim()}
                  className="nb-btn bg-brand-primary text-white flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {vetLoading ? "Matching…" : "Match my code"}
                </button>
              </div>
              {vetError && (
                <p data-testid="veteran-error" className="mt-3 border-2 border-black rounded-xl bg-white px-4 py-2 text-sm font-bold text-black">
                  {vetError}
                </p>
              )}
              {veteranMatch && (
                <div data-testid="veteran-match-result" className="mt-3 border-2 border-black rounded-xl bg-white p-4 shadow-nb-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="w-5 h-5 text-green-600" strokeWidth={3} />
                    <span className="font-heading text-lg font-bold">{veteranMatch.branch} · {veteranMatch.code} · {veteranMatch.rating_title}</span>
                  </div>
                  <p className="text-sm font-semibold text-black/70">
                    We'll start you in the <b>{veteranMatch.primary_interest_area?.title}</b> interest area with your most relevant activities first.
                  </p>
                  <p className="text-sm font-semibold text-black/70 mt-1">
                    Related civilian roles: {veteranMatch.civilian_examples.join(", ")}.
                  </p>
                  <button
                    data-testid="start-from-veteran-btn"
                    onClick={() => startPhase1(veteranMatch.primary_interest_area.id, veteranMatch)}
                    className="nb-btn bg-brand-secondary mt-4 flex items-center gap-2"
                  >
                    <Rocket className="w-4 h-4" /> Start with my job matches
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Always-available path — makes veteran flow optional */}
        <div className="flex items-center justify-between gap-3">
          <p className="font-semibold text-slate-600 text-sm">Not a veteran, or want to browse freely?</p>
          <button
            data-testid="skip-to-select-btn"
            onClick={() => setStep("select")}
            className="nb-btn bg-brand-primary text-white flex items-center gap-2"
          >
            Explore by interest <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // ---- Primary Interest Area selection ----
  if (step === "select") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button data-testid="back-to-onboarding-btn" onClick={resetAll} className="nb-badge bg-white mb-4">
            <ArrowLeft className="w-4 h-4" /> Start over
          </button>
          <span className="nb-badge bg-brand-yellow mb-3 ml-2"><Sparkles className="w-4 h-4" /> O*NET Interest Areas</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-black mb-2">Pick your Primary Interest Area</h1>
          <p className="text-base md:text-lg font-semibold text-slate-600 mb-8 max-w-2xl">
            Choose the one area that feels most like you. In <b>Phase 1</b> you'll rate activities from that area, then in <b>Phase 2</b> you'll explore the rest.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 animate-spin text-brand-primary" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {areas.map((area, i) => {
              const isSel = primary === area.id;
              return (
                <motion.button
                  key={area.id}
                  data-testid={`interest-area-${area.id}`}
                  onClick={() => setPrimary(area.id)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`nb-card p-6 text-left relative transition-all ${isSel ? "shadow-nb-lg -translate-x-[2px] -translate-y-[2px]" : ""}`}
                  style={{ backgroundColor: isSel ? area.color : "#FFFFFF" }}
                >
                  {isSel && (
                    <span className="absolute top-3 right-3 bg-black text-white rounded-full p-1">
                      <Check className="w-4 h-4" strokeWidth={3} />
                    </span>
                  )}
                  <div className="bg-white border-2 border-black rounded-xl p-3 shadow-nb-xs inline-flex mb-4">
                    <ActivityIcon name={area.icon} className="w-8 h-8 text-black" strokeWidth={2.2} />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-black/60 mb-0.5">{area.tagline}</p>
                  <h3 className="font-heading text-2xl font-bold text-black mb-1">{area.title}</h3>
                  <p className="text-sm font-semibold text-black/70">{area.description}</p>
                </motion.button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between sticky bottom-4">
          <span className="nb-badge bg-white" data-testid="primary-label">
            {primaryArea ? `Primary: ${primaryArea.title}` : "None selected"}
          </span>
          <button
            data-testid="start-phase1-btn"
            onClick={() => startPhase1()}
            disabled={!primary}
            className="nb-btn bg-brand-primary text-white flex items-center gap-2 disabled:opacity-50"
          >
            Start Phase 1 <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // ---- Transition: narrowing Phase 2 ----
  if (transitioning) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid="phase2-transition"
          className="nb-card p-8 bg-brand-secondary text-center"
        >
          <Loader2 className="w-12 h-12 animate-spin text-black mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-black mb-2">Nice work! 🎉</h2>
          <p data-testid="phase2-transition-message" className="font-semibold text-black/80">
            Since you loved these activities, let's take a look at some other activities that will help us to narrow down possible roles.
          </p>
        </motion.div>
      </div>
    );
  }

  // ---- Swipe steps (phase1 / phase2) ----
  const total = currentDeck.length;
  const done = Math.min(index, total);
  const progress = total ? Math.round((done / total) * 100) : 0;
  const remaining = currentDeck.slice(index, index + 3).reverse();
  const phaseColor = step === "phase1" ? (primaryArea?.color || "#FFE66D") : "#C7CEEA";
  const phase2Responses = responses.slice(phase1Cards.length);
  const dislikedIds = new Set(phase2Responses.filter((r) => r.preference === "dislike").map((r) => r.activity_id));
  const survivors = candidates.filter((c) => !(c.key_activities || []).some((a) => dislikedIds.has(a)));

  return (
    <div className="max-w-lg mx-auto px-4 py-6 md:py-8">
      <div className="mb-4">
        <button data-testid="back-to-select-btn" onClick={() => (veteranMatch ? resetAll() : setStep("select"))} className="nb-badge bg-white mb-4">
          <ArrowLeft className="w-4 h-4" /> {veteranMatch ? "Start over" : "Interest area"}
        </button>

        <motion.div
          key={step}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="phase-indicator"
          className="nb-card p-4 mb-4 flex items-center gap-3"
          style={{ backgroundColor: phaseColor }}
        >
          <div className="bg-white border-2 border-black rounded-lg p-2 shadow-nb-xs">
            <Layers className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-black/60">
              {step === "phase1" ? "Phase 1 of 2" : "Phase 2 of 2"}
            </p>
            <p className="font-heading text-lg font-bold text-black leading-tight">
              {step === "phase1"
                ? `Your primary area: ${primaryArea?.title}`
                : `Refining ${candidateCount} matching profession${candidateCount === 1 ? "" : "s"}`}
            </p>
          </div>
        </motion.div>

        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold uppercase tracking-wider">Activity {Math.min(done + (submitting ? 0 : 1), total)} of {total}</span>
          <span data-testid="progress-count" className="text-sm font-bold">{progress}%</span>
        </div>
        <div className="h-4 w-full bg-white border-2 border-black rounded-full overflow-hidden">
          <motion.div className="h-full bg-brand-yellow" animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 200, damping: 30 }} />
        </div>
      </div>

      <h1 className="font-heading text-2xl md:text-3xl font-bold text-black text-center mb-1">Do you enjoy this?</h1>
      <p className="text-center font-semibold text-slate-600 mb-6">Swipe right to love it, left to skip.</p>

      <div className="relative h-[420px] mb-6">
        {submitting ? (
          <div className="nb-card h-full flex flex-col items-center justify-center gap-4 bg-brand-secondary">
            <Loader2 className="w-10 h-10 animate-spin text-black" />
            <p className="font-heading text-xl font-bold">Finding your careers…</p>
          </div>
        ) : remaining.length === 0 ? (
          <div className="nb-card h-full flex items-center justify-center bg-brand-yellow">
            <p className="font-heading text-xl font-bold">Loading…</p>
          </div>
        ) : (
          <AnimatePresence>
            {remaining.map((act, i) => (
              <SwipeCard
                key={`${step}-${act.id}`}
                activity={act}
                index={remaining.length - 1 - i}
                isTop={i === remaining.length - 1}
                onDecision={decide}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {!submitting && remaining.length > 0 && (
        <div className="flex items-center justify-center gap-4">
          <button data-testid="dislike-button" onClick={() => decide("dislike")} className="nb-btn bg-white p-4 rounded-full" aria-label="Skip">
            <X className="w-7 h-7 text-black" strokeWidth={3} />
          </button>
          <button data-testid="superlike-button" onClick={() => decide("superlike")} className="nb-btn bg-brand-purple p-3 rounded-full" aria-label="Super love">
            <Sparkles className="w-6 h-6 text-black" strokeWidth={2.5} />
          </button>
          <button data-testid="like-button" onClick={() => decide("like")} className="nb-btn bg-brand-primary p-4 rounded-full" aria-label="Love it">
            <Heart className="w-7 h-7 text-white" strokeWidth={3} />
          </button>
        </div>
      )}

      {step === "phase2" && candidates.length > 0 && (
        <div className="mt-10" data-testid="shortlist-panel">
          <div className="flex items-center justify-between mb-3">
            <span className="nb-badge bg-brand-secondary"><Target className="w-4 h-4" /> Still in the running</span>
            <span data-testid="shortlist-count" className="font-heading text-3xl font-bold text-black">{survivors.length}</span>
          </div>
          <p className="text-sm font-semibold text-slate-600 mb-3">
            Professions matching what you love. Skip an activity a job relies on and it drops out.
          </p>
          <div className="flex flex-wrap gap-2 min-h-[2.5rem]">
            <AnimatePresence>
              {survivors.map((c) => (
                <motion.span
                  key={c.onet_code}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.2 } }}
                  data-testid={`shortlist-${c.onet_code}`}
                  className="nb-badge bg-white"
                >
                  <span className="w-2.5 h-2.5 rounded-full border border-black" style={{ backgroundColor: c.interest_area?.color || "#FFE66D" }} />
                  {c.title}
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
          {survivors.length === 0 && (
            <p data-testid="shortlist-empty" className="font-semibold text-slate-600 mt-1">
              No perfect matches left — we'll still rank your closest fits at the end.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
